import React, { useState, useEffect } from "react";
import ReactHtmlParser from "react-html-parser";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FastAverageColor } from "fast-average-color";
import { track } from "@herb/utils/analytics";

// Validation schema
const validationSchema = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email")
    .required("Email is required"),
  firstName: Yup.string()
    .required("First name is required")
    .min(2, "First name must be at least 2 characters"),
  lastName: Yup.string()
    .required("Last name is required")
    .min(2, "Last name must be at least 2 characters"),
  phoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(
      /^\+1[\s.-]?\(?[2-9]\d{2}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
      "Please enter a valid US phone number including +1."
    ),
  instagramHandle: Yup.string().matches(
    /^[a-zA-Z0-9._]{1,30}$/,
    "Please enter a valid Instagram handle"
  )
});

const AdventRewardPopup = ({
  reward,
  doorNumber,
  closedDoorMessage,
  formContent,
  onClose
}) => {
  const [showForm, setShowForm] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [textColor, setTextColor] = useState("#313131");
  const [errorMessage, setErrorMessage] = useState("");

  const handleCtaClick = () => {
    // Track CTA button click
    track("Advent Calendar CTA Click", {
      action: "Show Entry Form",
      doorNumber: doorNumber,
      utm_source: "herb-advent-calendar",
      utm_medium: "cta",
      utm_campaign: "advent-2025",
      utm_content: `door-${String(doorNumber).padStart(2, "0")}`
    });
    setShowForm(true);
  };

  const handleFormSubmit = (values, { setSubmitting, resetForm }) => {
    setErrorMessage("");
    // UTM Parameters for tracking
    const utmParams = {
      utm_source: "herb-advent-calendar",
      utm_medium: "cta",
      utm_campaign: "advent-2025",
      utm_content: `door-${String(doorNumber).padStart(2, "0")}`
    };

    // Prepare data for Klaviyo with proper structure
    const formData = {
      email: values.email || "",
      first_name: values.firstName || "",
      last_name: values.lastName || "",
      phone_number: values.phoneNumber,
      properties: {
        instagramHandle: values.instagramHandle,
        [`door-entry-${String(doorNumber).padStart(2, "0")}`]: true,
        ...utmParams
      }
    };

    // Track form submission in analytics
    track("Advent Calendar Form Submit", {
      form: "Advent Calendar Entry",
      doorNumber: doorNumber,
      ...utmParams
    });

    // Open window immediately (for Safari iOS compatibility) - will update URL after success
    let promoWindow = null;
    if (formContent?.promoUrl) {
      promoWindow = window.open("about:blank", "_blank");
    }

    const options = {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify(formData)
    };

    const url =
      "https://us-central1-herb-platform-v3.cloudfunctions.net/klaviyo?listId=T6MXJp";

    fetch(url, options)
      .then(response => {
        setSubmitting(false);

        if (response.ok) {
          setShowSuccessMessage(true);
          if (formContent?.promoUrl && promoWindow) {
            const promoUrl = new URL(formContent.promoUrl);
            const utmParams = new URLSearchParams({
              utm_source: "herb-advent-calendar",
              utm_medium: "cta",
              utm_campaign: "advent-2025",
              utm_content: `door-${String(doorNumber).padStart(2, "0")}-promo`
            });

            utmParams.forEach((value, key) => {
              promoUrl.searchParams.set(key, value);
            });

            track("Advent Calendar Promo Link Click", {
              doorNumber: doorNumber,
              promoUrl: promoUrl.toString(),
              utm_source: "herb-advent-calendar",
              utm_medium: "cta",
              utm_campaign: "advent-2025",
              utm_content: `door-${String(doorNumber).padStart(2, "0")}-promo`
            });

            // Update the window location to the actual promo URL
            promoWindow.location.href = promoUrl.toString();
          }
        } else {
          // Close the blank window if there was an error
          if (promoWindow) {
            promoWindow.close();
          }
          response.json().then(data => {
            console.log("Error response from Klaviyo:", data);
            if (
              data?.details?.errors[0]?.detail ===
              "A profile already exists with one of these identifiers."
            ) {
              setErrorMessage(
                "This phone number is already in use by another profile. Please use a different phone number."
              );
            } else {
              setErrorMessage(
                data?.details?.errors[0]?.detail ||
                  "There was an error submitting the form. Please check your inputs and try again."
              );
            }
          });
        }
        return response;
      })
      .catch(err => {
        console.error("Error submitting to Klaviyo:", err);
        setSubmitting(false);
        // Close the blank window if there was an error
        if (promoWindow) {
          promoWindow.close();
        }
      });
  };

  // Calculate text color based on background image brightness
  useEffect(() => {
    if (reward?.backgroundImage?.sourceUrl) {
      const fac = new FastAverageColor();

      fac
        .getColorAsync(reward.backgroundImage.sourceUrl)
        .then(color => {
          // Use a high threshold - if brightness is less than 185 (out of 255), use white text
          // This is very aggressive towards white text
          const brightness =
            (color.value[0] + color.value[1] + color.value[2]) / 3;
          setTextColor(brightness < 185 ? "white" : "#313131");
        })
        .catch(() => {
          // Fallback to dark text if image fails to load
          setTextColor("#313131");
        });
    } else {
      // Default to dark text if no background image
      setTextColor("#313131");
    }
  }, [reward?.backgroundImage?.sourceUrl]);

  const popupStyle = {};

  if (reward?.backgroundImage && !showForm) {
    popupStyle.backgroundImage = `url(${reward.backgroundImage?.sourceUrl})`;
  } else {
    popupStyle.background = "#FFFFFF";
  }

  const rewardHeadingStyle = {};
  if (reward?.heading?.backgroundColor) {
    rewardHeadingStyle.backgroundColor = reward.heading.backgroundColor;
  }
  if (reward?.heading?.color) {
    rewardHeadingStyle.color = reward.heading.color;
  }

  const titleStyle = {};
  if (reward?.title?.backgroundColor) {
    titleStyle.backgroundColor = reward.title.backgroundColor;
  }
  if (reward?.title?.color) {
    titleStyle.color = reward.title.color;
  }

  return (
    <div className="advc-reward-popup-overlay" onClick={onClose}>
      <div
        className={`advc-reward-popup-content ${
          closedDoorMessage
            ? "closed-door"
            : showForm
            ? "form-show"
            : "reward-show"
        }`}
        onClick={e => e.stopPropagation()}
        style={popupStyle}
      >
        {closedDoorMessage ? (
          <>
            <div className="advc-closed-door-message">{closedDoorMessage}</div>
            <svg
              className="closeDoorMessageSVG"
              width="392"
              height="238"
              viewBox="0 0 392 238"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M303.751 235.226H50.7896V237.226H303.751V235.226Z"
                fill="#8F999E"
              />
              <path
                d="M67.0884 204.488L69.2614 236.226H103.788L101.463 202.134L67.0884 204.488Z"
                fill="#8F999E"
              />
              <path
                d="M104.859 237.226H68.3275L66.0225 203.558L102.392 201.067L104.859 237.226ZM70.1955 235.226H102.718L100.533 203.2L68.1545 205.418L70.1955 235.226Z"
                fill="#8F999E"
              />
              <path
                d="M38.5156 236.226L78.4026 139.132L46.5186 129.583L2.47363 236.226H38.5156Z"
                fill="#8F999E"
              />
              <path
                d="M39.1865 237.226H0.979492L45.9405 128.365L79.7485 138.49L39.1865 237.226ZM3.96949 235.226H37.8455L77.0575 139.773L47.0975 130.8L3.96949 235.226Z"
                fill="#8F999E"
              />
              <path
                d="M119.907 236.226H157.464C157.321 234.71 157.192 233.162 157.08 231.57C155.84 213.151 157.383 199.396 161.652 190.347C165.97 181.304 172.45 176.492 181.091 175.911C189.541 175.301 196.445 179.205 201.754 187.616C207.111 196.034 210.365 209.422 211.653 227.847C211.849 230.759 211.974 233.547 212.035 236.226H250.088C250.098 232.651 249.99 229.002 249.729 225.257C248.924 213.16 246.905 202.008 243.703 191.904C240.502 181.8 236.008 173.167 230.214 166.054C224.372 158.933 217.25 153.53 208.847 149.845C200.39 146.2 190.636 144.711 179.524 145.466C168.214 146.24 158.611 149.026 150.668 153.818C142.773 158.616 136.368 164.906 131.591 172.757C126.766 180.6 123.412 189.736 121.427 200.199C119.49 210.669 118.896 221.972 119.741 234.124C119.79 234.83 119.851 235.526 119.907 236.226Z"
                fill="white"
              />
              <path
                d="M251.086 237.226H211.059L211.036 236.249C210.973 233.53 210.846 230.726 210.656 227.914C209.383 209.689 206.103 196.311 200.911 188.152C195.83 180.101 189.181 176.328 181.165 176.907C172.83 177.468 166.744 182.005 162.556 190.776C158.365 199.66 156.858 213.363 158.079 231.502C158.19 233.083 158.318 234.623 158.461 236.132L158.564 237.226H118.984L118.864 235.731C118.822 235.218 118.781 234.707 118.746 234.189C117.908 222.143 118.48 210.645 120.446 200.016C122.458 189.413 125.921 180.067 130.741 172.231C135.529 164.364 142.06 157.879 150.151 152.963C158.205 148.105 168.065 145.247 179.458 144.468C190.605 143.711 200.623 145.211 209.245 148.927C217.76 152.661 225.073 158.209 230.989 165.419C236.826 172.587 241.424 181.396 244.658 191.603C247.88 201.772 249.922 213.072 250.728 225.191C250.978 228.788 251.099 232.503 251.088 236.23L251.086 237.226ZM213.011 235.226H249.089C249.077 231.882 248.958 228.556 248.733 225.326C247.938 213.364 245.925 202.222 242.753 192.206C239.596 182.24 235.117 173.654 229.441 166.685C223.729 159.723 216.666 154.365 208.449 150.76C200.127 147.174 190.418 145.727 179.595 146.463C168.522 147.22 158.964 149.983 151.188 154.673C143.377 159.421 137.07 165.68 132.448 173.275C127.753 180.908 124.378 190.027 122.412 200.384C120.477 210.843 119.915 222.172 120.742 234.053C120.768 234.448 120.799 234.837 120.83 235.226H156.37C156.266 234.052 156.17 232.859 156.084 231.639C154.84 213.154 156.41 199.118 160.749 189.919C165.216 180.564 172.038 175.517 181.026 174.912C181.616 174.87 182.205 174.848 182.785 174.848C190.81 174.848 197.477 178.963 202.6 187.08C208.047 195.64 211.335 208.952 212.652 227.776C212.822 230.286 212.942 232.786 213.011 235.226Z"
                fill="#8F999E"
              />
              <path
                d="M389.914 236.226L387.371 220.47L369.444 221.685L366.726 181.829L332.351 184.184L335.077 223.991L299.463 226.457L343.713 118.834L311.781 109.278L259.35 236.226H389.914Z"
                fill="#8F999E"
              />
              <path
                d="M391.089 237.226H257.854L311.203 108.061L345.057 118.192L301 225.348L334.009 223.063L331.284 183.254L367.655 180.763L370.374 220.619L388.212 219.409L391.089 237.226ZM260.845 235.226H388.74L386.529 221.529L368.514 222.751L365.796 182.897L333.417 185.115L336.143 224.922L297.926 227.566L342.367 119.476L312.359 110.497L260.845 235.226Z"
                fill="#8F999E"
              />
              <path
                d="M195.73 155.029C196.074 159.292 187.38 163.471 176.312 164.362C165.244 165.254 155.993 162.521 155.65 158.258C155.306 153.995 164 149.816 175.068 148.924C186.136 148.033 195.386 150.766 195.73 155.029Z"
                fill="#E1E5E6"
              />
              <path
                d="M215.22 157.415C214.604 159.135 210.863 159.367 206.865 157.934C202.867 156.501 200.126 153.945 200.742 152.225C201.358 150.505 205.099 150.273 209.097 151.706C213.095 153.139 215.836 155.696 215.22 157.415Z"
                fill="#E1E5E6"
              />
              <path
                d="M164.864 156.153C164.862 156.153 164.86 156.153 164.858 156.153C163.477 156.15 162.361 155.028 162.364 153.647C162.416 131.82 155.908 117.499 154.238 114.836C153.902 114.507 153.655 114.08 153.547 113.586C153.342 112.65 153.691 111.643 154.444 111.05C155.198 110.458 156.235 110.349 157.096 110.768C159.445 111.913 162.962 122.977 162.997 123.089C165.007 129.516 167.397 140.036 167.364 153.659C167.36 155.038 166.241 156.153 164.864 156.153Z"
                fill="#8F999E"
              />
              <path
                d="M224.32 128.035C223.698 128.035 223.076 127.805 222.592 127.342C221.595 126.387 221.56 124.805 222.514 123.807C224.196 122.039 228.569 116.223 226.635 112.848C224.612 109.319 216.449 106.139 211.779 104.965C210.44 104.628 209.628 103.27 209.964 101.932C210.3 100.593 211.658 99.7829 212.995 100.115C214.442 100.478 227.239 103.844 230.973 110.363C235.167 117.683 226.498 126.877 226.127 127.265C225.635 127.776 224.978 128.035 224.32 128.035Z"
                fill="#8F999E"
              />
              <path
                d="M206.723 36.5798C206.33 36.5798 205.931 36.4868 205.56 36.2908C204.338 35.6468 203.87 34.1358 204.513 32.9138L221.14 1.34983C221.782 0.12883 223.296 -0.341167 224.517 0.302833C225.739 0.945833 226.207 2.45783 225.564 3.67983L208.937 35.2438C208.49 36.0948 207.621 36.5798 206.723 36.5798Z"
                fill="#8F999E"
              />
              <path
                d="M141.913 58.6379C141.826 58.6379 141.738 58.6339 141.65 58.6239L101.483 54.4179C100.11 54.2749 99.1125 53.0439 99.2565 51.6709C99.4005 50.2979 100.631 49.3059 102.004 49.4449L142.171 53.6499C143.544 53.7939 144.541 55.0239 144.397 56.3969C144.262 57.6829 143.177 58.6379 141.913 58.6379Z"
                fill="#8F999E"
              />
              <path
                d="M226.365 106.044L159.899 126.599C158.559 127.013 157.137 126.263 156.723 124.923L132.431 46.3727C132.233 45.7337 132.296 45.0417 132.605 44.4477L141.171 28.0227C141.484 27.4217 142.025 26.9707 142.672 26.7707L196.684 10.0678C198.024 9.65375 199.446 10.4037 199.86 11.7437L228.04 102.868C228.455 104.207 227.705 105.629 226.365 106.044Z"
                fill="white"
              />
              <path
                d="M159.147 127.713C157.587 127.713 156.229 126.711 155.767 125.219L131.474 46.6688C131.197 45.7718 131.283 44.8188 131.718 43.9868L140.284 27.5618C140.718 26.7298 141.481 26.0948 142.377 25.8168L196.389 9.11284C196.73 9.00784 197.082 8.95483 197.436 8.95483C198.997 8.95483 200.355 9.95684 200.816 11.4488L228.997 102.573C229.276 103.476 229.187 104.434 228.746 105.27C228.305 106.106 227.564 106.721 226.661 107L160.195 127.554C159.854 127.659 159.501 127.713 159.147 127.713ZM197.436 10.9538C197.283 10.9538 197.129 10.9768 196.98 11.0228L142.968 27.7268C142.578 27.8468 142.246 28.1238 142.058 28.4858L133.492 44.9108C133.303 45.2728 133.265 45.6868 133.386 46.0778L157.678 124.627C157.925 125.423 158.795 125.895 159.603 125.644L226.07 105.089C226.464 104.968 226.785 104.701 226.977 104.337C227.168 103.973 227.207 103.556 227.086 103.164L198.905 12.0398C198.704 11.3898 198.115 10.9538 197.436 10.9538Z"
                fill="#8F999E"
              />
              <path
                d="M182.524 58.0668C183.693 63.1138 181.446 68.6238 177.504 70.3758C173.562 72.1278 169.42 69.4558 168.251 64.4098C167.082 59.3628 169.331 53.8528 173.272 52.1008C177.213 50.3488 181.355 53.0198 182.524 58.0668Z"
                fill="#8F999E"
              />
              <path
                d="M173.272 52.1007C171.486 52.8937 170.054 54.4627 169.124 56.4067C170.969 58.2637 173.505 58.8988 175.966 57.8048C177.754 57.0108 179.182 55.4358 180.111 53.4878C178.266 51.6368 175.73 51.0077 173.272 52.1007Z"
                fill="#FFFCF3"
              />
              <path
                d="M141.732 27.3127C141.703 27.3387 141.679 27.3677 141.651 27.3947L132.448 44.8377C132.398 44.9907 132.364 45.1467 132.344 45.3057L143.621 41.8177C144.93 41.4127 145.662 40.0237 145.258 38.7157L141.732 27.3127Z"
                fill="#FFFCF3"
              />
              <path
                d="M131.158 46.7188L131.352 45.1798C131.38 44.9588 131.428 44.7418 131.497 44.5278L131.564 44.3698L140.95 26.6808C140.991 26.6368 141.03 26.5978 141.071 26.5618L142.231 25.5398L146.214 38.4198C146.488 39.3078 146.401 40.2498 145.967 41.0718C145.533 41.8938 144.805 42.4978 143.917 42.7728L131.158 46.7188ZM141.476 29.8698L134.184 43.6898L143.326 40.8628C143.703 40.7458 144.014 40.4888 144.198 40.1398C144.382 39.7898 144.42 39.3888 144.303 39.0118L141.476 29.8698Z"
                fill="#8F999E"
              />
              <path
                d="M185.105 41.0049C185.246 42.1599 184.424 43.2109 183.269 43.3519C182.114 43.4929 181.063 42.6719 180.922 41.5159C180.781 40.3609 181.603 39.3099 182.758 39.1689C183.913 39.0289 184.964 39.8499 185.105 41.0049Z"
                fill="#8F999E"
              />
              <path
                d="M164.439 48.8217C164.58 49.9767 163.759 51.0277 162.603 51.1687C161.448 51.3097 160.397 50.4877 160.256 49.3327C160.115 48.1777 160.936 47.1267 162.092 46.9857C163.246 46.8447 164.297 47.6667 164.439 48.8217Z"
                fill="#8F999E"
              />
            </svg>
          </>
        ) : showForm ? (
          <div className="advc-form-contents">
            {formContent?.formImage && (
              <img
                className="advc-form-image"
                src={formContent?.formImage?.sourceUrl}
                alt={formContent?.formImage?.altText}
              />
            )}
            {formContent?.formHeading && (
              <h2 className="advc-form-heading">{formContent?.formHeading}</h2>
            )}
            {formContent?.formSubheading && (
              <h3 className="advc-form-subheading">
                {formContent?.formSubheading}
              </h3>
            )}

            <Formik
              initialValues={{
                email: "",
                firstName: "",
                lastName: "",
                phoneNumber: "",
                instagramHandle: ""
              }}
              validationSchema={validationSchema}
              onSubmit={handleFormSubmit}
            >
              {({ isSubmitting, errors, touched }) => (
                <Form className="advc-form">
                  {showSuccessMessage ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        color: "#28a745",
                        fontSize: "18px",
                        fontWeight: "600"
                      }}
                    >
                      ✓ Your entry for today has been recorded! Thank you for
                      entering.
                    </div>
                  ) : (
                    <>
                      <div className="advc-form-row">
                        <div className="advc-form-field">
                          <label htmlFor="email" className="advc-form-label">
                            Email
                          </label>
                          <Field
                            type="email"
                            id="email"
                            name="email"
                            className={`advc-form-input ${
                              errors.email && touched.email ? "error" : ""
                            }`}
                            placeholder=""
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="advc-form-error"
                          />
                        </div>
                      </div>

                      <div className="advc-form-row">
                        <div className="advc-form-field">
                          <label
                            htmlFor="firstName"
                            className="advc-form-label"
                          >
                            First Name
                          </label>
                          <Field
                            type="text"
                            id="firstName"
                            name="firstName"
                            className={`advc-form-input ${
                              errors.firstName && touched.firstName
                                ? "error"
                                : ""
                            }`}
                            placeholder=""
                          />
                          <ErrorMessage
                            name="firstName"
                            component="div"
                            className="advc-form-error"
                          />
                        </div>

                        <div className="advc-form-field">
                          <label htmlFor="lastName" className="advc-form-label">
                            Last Name
                          </label>
                          <Field
                            type="text"
                            id="lastName"
                            name="lastName"
                            className={`advc-form-input ${
                              errors.lastName && touched.lastName ? "error" : ""
                            }`}
                            placeholder=""
                          />
                          <ErrorMessage
                            name="lastName"
                            component="div"
                            className="advc-form-error"
                          />
                        </div>
                      </div>

                      <div className="advc-form-row">
                        <div className="advc-form-field">
                          <label
                            htmlFor="phoneNumber"
                            className="advc-form-label"
                          >
                            Phone Number
                          </label>
                          <Field
                            type="tel"
                            id="phoneNumber"
                            name="phoneNumber"
                            className={`advc-form-input ${
                              errors.phoneNumber && touched.phoneNumber
                                ? "error"
                                : ""
                            }`}
                          />
                          <ErrorMessage
                            name="phoneNumber"
                            component="div"
                            className="advc-form-error"
                          />
                          {/* <span className="advc-form-note">
                            Please enter a valid phone number.
                          </span> */}
                        </div>

                        <div className="advc-form-field">
                          <label
                            htmlFor="instagramHandle"
                            className="advc-form-label"
                          >
                            Instagram Handle
                          </label>
                          <Field
                            type="text"
                            id="instagramHandle"
                            name="instagramHandle"
                            className={`advc-form-input ${
                              errors.instagramHandle && touched.instagramHandle
                                ? "error"
                                : ""
                            }`}
                            placeholder=""
                          />
                          <ErrorMessage
                            name="instagramHandle"
                            component="div"
                            className="advc-form-error"
                          />
                        </div>
                      </div>
                      {errorMessage && (
                        <span className="advc-form-error advc-form-submit-error">
                          {errorMessage}
                        </span>
                      )}
                      <button
                        type="submit"
                        className="advc-form-cta"
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? "Submitting..."
                          : formContent?.ctaLabel || "Submit"}
                      </button>
                    </>
                  )}
                </Form>
              )}
            </Formik>
            <span className="advc-form-fText">
              {ReactHtmlParser(formContent?.formFooterText)}
            </span>
          </div>
        ) : (
          <>
            <div className="advc-reward-overlay"> </div>
            <div className="advc-reward-content">
              <p
                className="advc-reward-reveal-text"
                style={{ color: textColor }}
              >
                Day {doorNumber} Revealed
              </p>
              {reward?.heading && (
                <h2 className="advc-reward-heading" style={rewardHeadingStyle}>
                  {reward.heading?.text}
                </h2>
              )}
              {reward?.title && (
                <h2 className="advc-reward-title" style={titleStyle}>
                  {reward.title?.text}
                </h2>
              )}
              {reward?.discount?.percentage && reward?.discount?.code && (
                <p className="advc-reward-discount-text">
                  Claim{" "}
                  <span className="advc-reward-colored-text">
                    {reward?.discount?.percentage}% off
                  </span>{" "}
                  with code{" "}
                  <span className="advc-reward-colored-text">
                    {reward?.discount?.code}
                  </span>
                </p>
              )}
            </div>
            <div className="advc-reward-content">
              {reward?.text && (
                <div
                  className="advc-reward-text"
                  style={{
                    color: reward.text?.color || textColor,
                    backgroundColor:
                      reward.text?.backgroundColor || "transparent"
                  }}
                >
                  {ReactHtmlParser(reward.text?.text)}
                </div>
              )}
              <button className="advc-reward-cta" onClick={handleCtaClick}>
                {reward?.ctaLabel || "I'm feeling lucky"}{" "}
                <svg
                  width="13"
                  height="12"
                  viewBox="0 0 13 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.72754 5.32617C1.31333 5.32617 0.977539 5.66196 0.977539 6.07617C0.977539 6.49039 1.31333 6.82617 1.72754 6.82617L1.72754 6.07617L1.72754 5.32617ZM12.1612 6.6065C12.4541 6.31361 12.4541 5.83873 12.1612 5.54584L7.38822 0.772871C7.09532 0.479977 6.62045 0.479977 6.32756 0.772871C6.03466 1.06576 6.03466 1.54064 6.32756 1.83353L10.5702 6.07617L6.32756 10.3188C6.03467 10.6117 6.03467 11.0866 6.32756 11.3795C6.62045 11.6724 7.09533 11.6724 7.38822 11.3795L12.1612 6.6065ZM1.72754 6.07617L1.72754 6.82617L11.6309 6.82617L11.6309 6.07617L11.6309 5.32617L1.72754 5.32617L1.72754 6.07617Z"
                    fill="#313131"
                  />
                </svg>
              </button>
            </div>
          </>
        )}
        <span className="advc-reward-popup-close" onClick={onClose}>
          <svg
            class="sideDrawerCross"
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="24"
            viewBox="0 0 26 24"
            fill="none"
          >
            <ellipse cx="12.5714" cy="12" rx="12.5714" ry="12"></ellipse>
            <line
              x1="7.21"
              y1="6.50387"
              x2="18.6386"
              y2="17.9324"
              stroke={showForm ? "#313131" : textColor}
            ></line>
            <line
              x1="7.21"
              y1="6.50387"
              x2="18.6386"
              y2="17.9324"
              stroke={showForm ? "#313131" : textColor}
            ></line>
            <line
              x1="7.21"
              y1="6.50387"
              x2="18.6386"
              y2="17.9324"
              stroke={showForm ? "#313131" : textColor}
            ></line>
            <line
              x1="18.6387"
              y1="6.06791"
              x2="7.21014"
              y2="17.4965"
              stroke={showForm ? "#313131" : textColor}
            ></line>
            <line
              x1="18.6387"
              y1="6.06791"
              x2="7.21014"
              y2="17.4965"
              stroke={showForm ? "#313131" : textColor}
            ></line>
            <line
              x1="18.6387"
              y1="6.06791"
              x2="7.21014"
              y2="17.4965"
              stroke={showForm ? "#313131" : textColor}
            ></line>
          </svg>
        </span>
      </div>
    </div>
  );
};

export default AdventRewardPopup;
